import * as React from 'react';
import { Button } from './ui/button';
import { ArrowRight, AlertCircle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OrganicShapes } from './decor/OrganicShapes';

function AbstractDataViz() {
  return (
    <svg
      viewBox="0 0 600 500"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '500px', display: 'block' }}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="heroBg" cx="58%" cy="44%" r="72%">
          <stop offset="0%" stopColor="#1a3a5c" />
          <stop offset="100%" stopColor="#0a1628" />
        </radialGradient>
        <filter id="glow-sm" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-lg" x="-120%" y="-120%" width="340%" height="340%">
          <feGaussianBlur stdDeviation="14" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="bottomFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="35%" stopColor="#0a1628" stopOpacity="0" />
          <stop offset="100%" stopColor="#0a1628" stopOpacity="0.75" />
        </linearGradient>
        <style>{`
          @keyframes pulse-a {
            0%   { r: 46; opacity: 0.22; }
            100% { r: 100; opacity: 0; }
          }
          @keyframes pulse-b {
            0%   { r: 46; opacity: 0.14; }
            100% { r: 140; opacity: 0; }
          }
          @keyframes node-breathe {
            0%, 100% { opacity: 0.92; }
            50%       { opacity: 0.65; }
          }
          @keyframes packet-1 {
            0%   { cx: 130; cy: 150; opacity: 0; }
            8%   { opacity: 1; }
            92%  { opacity: 1; }
            100% { cx: 390; cy: 240; opacity: 0; }
          }
          @keyframes packet-2 {
            0%   { cx: 510; cy: 110; opacity: 0; }
            8%   { opacity: 1; }
            92%  { opacity: 1; }
            100% { cx: 390; cy: 240; opacity: 0; }
          }
          @keyframes packet-3 {
            0%   { cx: 390; cy: 240; opacity: 0; }
            8%   { opacity: 1; }
            92%  { opacity: 1; }
            100% { cx: 490; cy: 390; opacity: 0; }
          }
          @keyframes packet-4 {
            0%   { cx: 155; cy: 380; opacity: 0; }
            8%   { opacity: 1; }
            92%  { opacity: 1; }
            100% { cx: 390; cy: 240; opacity: 0; }
          }
          .ring-a { animation: pulse-a 3.2s ease-out infinite; }
          .ring-b { animation: pulse-b 3.2s ease-out 1.6s infinite; }
          .main-node { animation: node-breathe 3s ease-in-out infinite; }
          .pkt-1 { animation: packet-1 3s linear infinite; }
          .pkt-2 { animation: packet-2 3s linear 1.5s infinite; }
          .pkt-3 { animation: packet-3 2.8s linear 0.8s infinite; }
          .pkt-4 { animation: packet-4 3.4s linear 2.1s infinite; }
        `}</style>
      </defs>

      {/* Background */}
      <rect width="600" height="500" fill="url(#heroBg)" />

      {/* Dot grid */}
      <g opacity="0.12">
        {[60,120,180,240,300,360,420,480].map(y =>
          [40,100,160,220,280,340,400,460,520,580].map(x => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1" fill="#34AFFB" />
          ))
        )}
      </g>

      {/* Connection lines */}
      <line x1="390" y1="240" x2="130" y2="150" stroke="#34AFFB" strokeWidth="1" opacity="0.22" />
      <line x1="390" y1="240" x2="510" y2="110" stroke="#34AFFB" strokeWidth="1" opacity="0.20" />
      <line x1="390" y1="240" x2="155" y2="380" stroke="#34AFFB" strokeWidth="1" opacity="0.18" />
      <line x1="390" y1="240" x2="490" y2="390" stroke="#70D6C5" strokeWidth="1" opacity="0.18" />
      <line x1="390" y1="240" x2="275" y2="72"  stroke="#70D6C5" strokeWidth="1" opacity="0.16" />
      <line x1="390" y1="240" x2="558" y2="268" stroke="#34AFFB" strokeWidth="1" opacity="0.15" />
      <line x1="130" y1="150" x2="275" y2="72"  stroke="#70D6C5" strokeWidth="0.6" opacity="0.12" />
      <line x1="510" y1="110" x2="558" y2="268" stroke="#34AFFB" strokeWidth="0.6" opacity="0.12" />
      <line x1="155" y1="380" x2="490" y2="390" stroke="#34AFFB" strokeWidth="0.6" opacity="0.10" />
      <line x1="130" y1="150" x2="62"  y2="238" stroke="#34AFFB" strokeWidth="0.6" opacity="0.11" />
      <line x1="155" y1="380" x2="62"  y2="238" stroke="#70D6C5" strokeWidth="0.6" opacity="0.10" />

      {/* Animated data packets */}
      <circle r="3"   fill="#34AFFB" className="pkt-1" />
      <circle r="2.5" fill="#70D6C5" className="pkt-2" />
      <circle r="2.5" fill="#70D6C5" className="pkt-3" />
      <circle r="2"   fill="#34AFFB" className="pkt-4" />

      {/* Pulse rings */}
      <circle cx="390" cy="240" fill="none" stroke="#34AFFB" strokeWidth="1.5" className="ring-a" />
      <circle cx="390" cy="240" fill="none" stroke="#34AFFB" strokeWidth="1"   className="ring-b" />

      {/* Secondary nodes */}
      <circle cx="130" cy="150" r="15" fill="#70D6C5" opacity="0.78" filter="url(#glow-sm)" />
      <circle cx="510" cy="110" r="12" fill="#34AFFB" opacity="0.72" filter="url(#glow-sm)" />
      <circle cx="155" cy="380" r="11" fill="#34AFFB" opacity="0.68" filter="url(#glow-sm)" />
      <circle cx="490" cy="390" r="14" fill="#70D6C5" opacity="0.72" filter="url(#glow-sm)" />
      <circle cx="275" cy="72"  r="9"  fill="#70D6C5" opacity="0.62" filter="url(#glow-sm)" />
      <circle cx="558" cy="268" r="8"  fill="#34AFFB" opacity="0.58" filter="url(#glow-sm)" />
      <circle cx="62"  cy="238" r="6"  fill="#34AFFB" opacity="0.52" />

      {/* Tiny accent nodes */}
      <circle cx="230" cy="315" r="4"   fill="#70D6C5" opacity="0.42" />
      <circle cx="348" cy="432" r="4.5" fill="#34AFFB" opacity="0.35" />
      <circle cx="458" cy="44"  r="5"   fill="#70D6C5" opacity="0.45" />
      <circle cx="578" cy="172" r="3.5" fill="#34AFFB" opacity="0.38" />
      <circle cx="108" cy="68"  r="4.5" fill="#70D6C5" opacity="0.42" />
      <circle cx="318" cy="192" r="3"   fill="#34AFFB" opacity="0.33" />
      <circle cx="444" cy="174" r="3.5" fill="#70D6C5" opacity="0.38" />

      {/* Main node */}
      <circle cx="390" cy="240" r="42" fill="#34AFFB" opacity="0.08" filter="url(#glow-lg)" />
      <circle cx="390" cy="240" r="34" fill="none" stroke="#34AFFB" strokeWidth="1.5" opacity="0.35" />
      <circle cx="390" cy="240" r="28" fill="#34AFFB" className="main-node" filter="url(#glow-lg)" />
      <circle cx="390" cy="240" r="14" fill="white" opacity="0.92" />
      <line x1="384" y1="234" x2="384" y2="246" stroke="#34AFFB" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="396" y1="234" x2="396" y2="246" stroke="#34AFFB" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="384" y1="240" x2="396" y2="240" stroke="#34AFFB" strokeWidth="2.2" strokeLinecap="round" />

      {/* Bottom fade */}
      <rect width="600" height="500" fill="url(#bottomFade)" />
    </svg>
  );
}

export function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9]">
      <OrganicShapes variant="corner" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-5">
              <h1 className="text-5xl lg:text-6xl text-[#1f2937] leading-tight">
                Foresight in.
                <span className="block text-[#34AFFB]">Friction out.</span>
              </h1>
              <p className="text-xl text-[#4B5563] leading-relaxed max-w-lg">
                Time returned to care. <br/><br/>
                PleromaHealth is a healthcare AI company delivering outcomes as software you own, built on one unified source of truth, so problems surface while there's still time to act.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white">
                <Link to="/#contact">
                  Book a demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-[#4B5563]/30 hover:bg-[#F4F7F9] hover:text-[#34AFFB]">
                <Link to="/healthbridge">Explore HealthBridge</Link>
              </Button>
            </div>

            <div className="pt-8 border-t border-[#4B5563]/20">
              <div className="grid md:grid-cols-2" style={{ gap: '20px 24px' }}>
                {[
                  { prefix: 'Up to',      stat: '25%',     label: 'Care-gap closure time' },
                  { prefix: 'Up to',      stat: '15%',     label: 'HCC recapture rate' },
                  { prefix: 'Up to',      stat: '40%',     label: 'Faster time-to-insight' },
                  { prefix: 'On average', stat: '$12,614', label: 'Revenue recaptured per high-risk member' },
                ].map((item, i) => (
                  <div key={i} style={{ borderLeft: '2px solid #34AFFB', paddingLeft: '12px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(75,85,99,0.5)', lineHeight: 1, marginBottom: '4px' }}>{item.prefix}</p>
                    <div style={{ fontSize: '30px', fontWeight: 700, color: '#34AFFB', lineHeight: 1 }}>{item.stat}</div>
                    <p style={{ fontSize: '13px', color: 'rgba(75,85,99,0.75)', marginTop: '5px', lineHeight: 1.3 }}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — abstract data visualization */}
          <div className="hidden lg:block">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <AbstractDataViz />

              {/* Floating card — top right */}
              <div style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(255,255,255,0.96)', borderRadius: '14px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', padding: '11px 15px', display: 'flex', alignItems: 'flex-start', gap: '10px', maxWidth: '205px' }}>
                <div style={{ height: '28px', width: '28px', background: '#FEF3C7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                  <AlertCircle className="h-3.5 w-3.5" style={{ color: '#F59E0B' }} />
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>3 HCC gaps at risk</p>
                  <p style={{ fontSize: '11px', color: 'rgba(75,85,99,0.65)', marginTop: '3px' }}>RAF target window closing</p>
                </div>
              </div>

              {/* Floating card — bottom left */}
              <div style={{ position: 'absolute', bottom: '20px', left: '20px', background: 'rgba(255,255,255,0.96)', borderRadius: '14px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', padding: '11px 15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <TrendingUp className="h-3.5 w-3.5" style={{ color: '#70D6C5' }} />
                  <p style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(75,85,99,0.5)' }}>Care gaps closed</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>23</span>
                  <span style={{ fontSize: '13px', color: 'rgba(75,85,99,0.5)' }}>this week</span>
                </div>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#70D6C5', marginTop: '4px' }}>↑ 18% vs last week</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
